import type { PermitResponse } from '@contracts/permitSearch/PermitListResponse';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import type { GridApi } from '@mui/x-data-grid-pro';
import { Button } from '@src/common/components/button/Button';
import { Stack } from '@src/common/components/Stack';
import { ToastLevel } from '@src/common/components/ToastLevel';
import { useToast } from '@src/common/hooks/useToast';
import { DownloadService } from '@src/common/services/DownloadService';
import { theme } from '@src/common/styles/Theme';
import { useMergePdfs } from '@src/permits/hooks/useMergePdfs';
import { useTranslation } from 'react-i18next';

interface Props {
  permits: PermitResponse[];
  gridApiRef: React.MutableRefObject<GridApi>;
}

export const PdfExportButton = ({ permits, gridApiRef }: Props) => {
  const { t } = useTranslation();
  const { showToast } = useToast();

  const { mergePdfs, isMergingPdfs } = useMergePdfs({
    onSuccess: (pdfBlob: Blob) => {
      DownloadService.openInNewTab(pdfBlob);
    },
    onError: () => {
      showToast({
        severity: ToastLevel.error,
        message: t('export.pdfGenerationError'),
      });
    },
  });

  const getPermitsWithDocuments = () => {
    if (!permits) return [];
    return permits.filter((permit) => permit.statePermitDocument);
  };

  const getSortedPermitIds = () => {
    const datagridSortedRowIds = gridApiRef.current
      .getSortedRowIds()
      .map((id) => String(id));

    const permitsWithDocsMap = new Map(
      getPermitsWithDocuments().map((p) => [p.id, p]),
    );

    return datagridSortedRowIds.filter((id) => permitsWithDocsMap.has(id));
  };

  const handleExportPdf = () => {
    mergePdfs(getSortedPermitIds());
  };

  const permitsWithDocuments = getPermitsWithDocuments();
  const hasDocuments = permitsWithDocuments.length > 0;

  return (
    <Stack alignItems="flex-start">
      <Button
        size="small"
        variant="contained"
        onClick={handleExportPdf}
        disabled={!hasDocuments || isMergingPdfs}
      >
        <PictureAsPdfIcon
          fontSize="small"
          sx={{ marginRight: theme.spacing['2'] }}
        />
        {isMergingPdfs
          ? t('export.exportingPdf')
          : `${t('export.exportPdf')} (${permitsWithDocuments.length})`}
      </Button>
    </Stack>
  );
};
